/**
 * PitchersWidget — Organized probable pitcher display with deduplication
 * - Fetches from #probable-pitchers channel
 * - Deduplicates by game matchup (keeps latest announcement)
 * - Groups by game date
 * - Shows away vs home pitcher with team logos and venue
 * Refresh: 60s | CSS prefix: ptw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFromProxy, imageUrl } from '../lib/api'
import { findTeam } from '../lib/teamLogos'
import { RefreshCw, Target, AlertCircle } from 'lucide-react'
import './PitchersWidget.css'

/** Parse date from title like "Tuesday, March 24 . Pitcher Announcements" */
function parseDateFromTitle(title) {
  if (!title) return null
  const clean = title.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim()
  const m = clean.match(/\w+,\s+(\w+)\s+(\d{1,2})/)
  if (!m) return null
  const months = { January: 0, February: 1, March: 2, April: 3, May: 4, June: 5, July: 6, August: 7, September: 8, October: 9, November: 10, December: 11 }
  const month = months[m[1]]
  if (month === undefined) return null
  const day = parseInt(m[2], 10)
  const now = new Date()
  let year = now.getFullYear()
  if (month < now.getMonth() - 6) year++
  return new Date(year, month, day)
}

/** Parse date from field value containing "Wed Mar 25" etc */
function parseDateFromFieldValue(value) {
  if (!value) return null
  const clean = value.replace(/[^\w\s:./]/g, ' ').trim()
  const m = clean.match(/\w{3}\s+(\w{3})\s+(\d{1,2})/)
  if (!m) return null
  const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
  const month = months[m[1]]
  if (month === undefined) return null
  const day = parseInt(m[2], 10)
  const now = new Date()
  let year = now.getFullYear()
  if (month < now.getMonth() - 6) year++
  return new Date(year, month, day)
}

/** Parse time from field value */
function parseTimeFromValue(value) {
  if (!value) return null
  const clean = value.replace(/[^\w\s:./]/g, ' ').trim()
  const m = clean.match(/(\d{1,2}:\d{2}\s*(?:AM|PM)\s*ET)/i)
  return m ? m[1].trim() : null
}

/** Parse venue from field value */
function parseVenueFromValue(value) {
  if (!value) return null
  // Look for stadium emoji or "Stadium:" prefix
  const m = value.match(/(?:\u{1F3DF}\uFE0F?|Stadium:)\s*(.+)/u)
  if (m) return m[1].trim()
  return null
}

/** Parse pitcher names from field value */
function parsePitchers(value) {
  if (!value) return { away: null, home: null }
  const awayMatch = value.match(/\*\*Away:\*\*\s*(.+)/i)
  const homeMatch = value.match(/\*\*Home:\*\*\s*(.+)/i)
  return {
    away: awayMatch ? awayMatch[1].trim() : null,
    home: homeMatch ? homeMatch[1].trim() : null,
  }
}

/** Parse matchup from field name like "Sultanes de Monterrey @ San Francisco Giants" */
function parseMatchupFromFieldName(name) {
  if (!name) return null
  const m = name.match(/(.+?)\s*[@]\s*(.+)/)
  if (m) return { awayText: m[1].trim(), homeText: m[2].trim() }
  return null
}

/** Parse matchup from content text like "**Sultanes de Monterrey @ San Francisco Giants**" */
function parseMatchupFromContent(text) {
  if (!text) return null
  const clean = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').replace(/\*\*/g, '').replace(/—.*/g, '').trim()
  const m = clean.match(/(.+?)\s*[@]\s*(.+)/)
  if (m) return { awayText: m[1].trim(), homeText: m[2].trim() }
  return null
}

/** Parse pitcher names from graphic content like "🎯 Justus Sheffield vs Landen Roupp" */
function parsePitchersFromContent(text) {
  if (!text) return { away: null, home: null }
  // Format: "⚾ **Team @ Team** — Date • Time / 🎯 AwayPitcher vs HomePitcher"
  const m1 = text.match(/🎯\s*(.+?)\s+vs\.?\s+(.+?)\s*$/)
  if (m1) return { away: m1[1].trim(), home: m1[2].trim() }
  // Fallback: "— Pitcher vs Pitcher" or "– Pitcher vs Pitcher"
  const m2 = text.match(/[—–\/]\s*(.+?)\s+vs\.?\s+(.+?)(?:\s*$|\s*[—–])/i)
  if (m2) return { away: m2[1].trim(), home: m2[2].trim() }
  return { away: null, home: null }
}

function matchupKey(awayText, homeText) {
  const normalize = (s) => s.toLowerCase().replace(/[^a-z]/g, '')
  return `${normalize(awayText)}@${normalize(homeText)}`
}

function formatDateLabel(date) {
  if (!date) return 'Unknown Date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const diff = (d - today) / 86400000
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** Format pitcher name — replace TBD with user-friendly text */
function formatPitcher(name) {
  if (!name || name.toUpperCase() === 'TBD') return 'Not Announced'
  return name
}

export default function PitchersWidget() {
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchData = useCallback(async () => {
    const { data, status, message } = await fetchFromProxy('/posts', { channel: '#probable-pitchers', limit: '100' })
    if (status !== 'ok' || !data) {
      setError(message || 'Failed to load pitchers')
      setLoading(false)
      return
    }

    const posts = Array.isArray(data) ? data : (data.posts || [])
    const gameMap = new Map()

    for (const post of posts) {
      // Handle graphic posts — pitcher card images
      if (post.post_type === 'graphic' && post.image_url) {
        const titleText = post.content || post.title || ''
        const matchup = parseMatchupFromContent(titleText)
        if (!matchup) continue

        // Filter out non-MLB teams (preseason/exhibition)
        const awayCheck = findTeam(matchup.awayText)
        const homeCheck = findTeam(matchup.homeText)
        if (!awayCheck || !homeCheck) continue

        const key = matchupKey(matchup.awayText, matchup.homeText)
        const existing = gameMap.get(key)

        if (existing) {
          if (!existing.imageUrl || new Date(post.timestamp) > new Date(existing.imageTimestamp || 0)) {
            existing.imageUrl = imageUrl(post.image_url)
            existing.imageTimestamp = post.timestamp
          }
        } else {
          const dateObj = parseDateFromFieldValue(titleText)
          const gameTime = parseTimeFromValue(titleText)
          const awayTeam = awayCheck
          const homeTeam = homeCheck
          // Try to parse pitcher names from content text
          const contentPitchers = parsePitchersFromContent(titleText)
          gameMap.set(key, {
            key, awayTeam, homeTeam,
            awayPitcher: contentPitchers.away, homePitcher: contentPitchers.home,
            gameTime, venue: null, dateObj, timestamp: post.timestamp,
            imageUrl: imageUrl(post.image_url), imageTimestamp: post.timestamp,
          })
        }
        continue
      }

      if (post.post_type !== 'text_embed' || !post.fields?.length) continue
      // Skip summary/progress posts (e.g. "Pitcher Update")
      if (post.title?.includes('Pitcher Update') || post.title?.includes('Progress')) continue

      const titleDate = parseDateFromTitle(post.title)

      for (const field of post.fields) {
        const matchup = parseMatchupFromFieldName(field.name)
        if (!matchup) continue

        // Filter out non-MLB teams (preseason/exhibition)
        const awayTeam = findTeam(matchup.awayText)
        const homeTeam = findTeam(matchup.homeText)
        if (!awayTeam || !homeTeam) continue

        const key = matchupKey(matchup.awayText, matchup.homeText)
        const existing = gameMap.get(key)
        if (existing?.awayPitcher && new Date(existing.timestamp) > new Date(post.timestamp)) continue

        const dateObj = parseDateFromFieldValue(field.value) || titleDate
        const gameTime = parseTimeFromValue(field.value)
        const venue = parseVenueFromValue(field.value)
        const pitchers = parsePitchers(field.value)

        const prevImage = existing?.imageUrl || null
        const prevImageTs = existing?.imageTimestamp || null

        gameMap.set(key, {
          key, awayTeam, homeTeam,
          awayPitcher: pitchers.away, homePitcher: pitchers.home,
          gameTime, venue, dateObj, timestamp: post.timestamp,
          imageUrl: prevImage, imageTimestamp: prevImageTs,
        })
      }
    }

    // Group by date
    const grouped = {}
    for (const game of gameMap.values()) {
      const dateKey = game.dateObj ? game.dateObj.toISOString().slice(0, 10) : 'unknown'
      if (!grouped[dateKey]) grouped[dateKey] = []
      grouped[dateKey].push(game)
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = today.toISOString().slice(0, 10)

    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === todayStr) return -1
      if (b === todayStr) return 1
      return new Date(a) - new Date(b)
    })

    const result = sortedDates.map(dateKey => ({
      dateKey,
      dateLabel: formatDateLabel(dateKey === 'unknown' ? null : new Date(dateKey + 'T12:00:00')),
      games: grouped[dateKey].sort((a, b) => {
        if (!a.gameTime && !b.gameTime) return 0
        if (!a.gameTime) return 1
        if (!b.gameTime) return -1
        return a.gameTime.localeCompare(b.gameTime)
      }),
    }))

    setGames(result)
    setError(null)
    setLoading(false)
    setLastUpdated(new Date())
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const totalGames = games.reduce((sum, g) => sum + g.games.length, 0)

  return (
    <div className="ptw-widget">
      <div className="ptw-header">
        <div className="ptw-header-left">
          <Target size={18} className="ptw-header-icon" />
          <h2 className="ptw-title">Probable Pitchers</h2>
          <span className="ptw-count">{totalGames} game{totalGames !== 1 ? 's' : ''}</span>
        </div>
        <div className="ptw-header-right">
          {lastUpdated && (
            <span className="ptw-last-updated">{lastUpdated.toLocaleTimeString()}</span>
          )}
          <button className="ptw-refresh" onClick={fetchData} title="Refresh">
            <RefreshCw size={14} className={loading ? 'ptw-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="ptw-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && !games.length ? (
        <div className="ptw-loading">Loading pitchers...</div>
      ) : (
        <div className="ptw-body">
          {games.length === 0 && !loading && (
            <div className="ptw-empty">No pitcher data available</div>
          )}

          {games.map(group => (
            <div key={group.dateKey} className="ptw-date-group">
              <div className="ptw-date-header">
                <span className="ptw-date-label">{group.dateLabel}</span>
                <span className="ptw-date-count">{group.games.length} game{group.games.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="ptw-games-grid">
                {group.games.map(game => (
                  <div key={game.key} className="ptw-game-card">
                    <div className="ptw-matchup">
                      <div className="ptw-side ptw-away">
                        {game.awayTeam?.logo && <img src={game.awayTeam.logo} alt="" className="ptw-team-logo" />}
                        <div className="ptw-side-info">
                          <span className="ptw-team-name">{game.awayTeam?.short || game.awayTeam?.abbr || game.awayTeam?.name}</span>
                          <span className="ptw-pitcher-name">{formatPitcher(game.awayPitcher)}</span>
                        </div>
                      </div>

                      <div className="ptw-vs">
                        <span className="ptw-vs-text">vs</span>
                      </div>

                      <div className="ptw-side ptw-home">
                        {game.homeTeam?.logo && <img src={game.homeTeam.logo} alt="" className="ptw-team-logo" />}
                        <div className="ptw-side-info">
                          <span className="ptw-team-name">{game.homeTeam?.short || game.homeTeam?.abbr || game.homeTeam?.name}</span>
                          <span className="ptw-pitcher-name">{formatPitcher(game.homePitcher)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pitcher card image from Discord */}
                    {game.imageUrl && (
                      <div className="ptw-image-container">
                        <img
                          src={game.imageUrl}
                          alt="Pitcher card"
                          className="ptw-pitcher-image"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      </div>
                    )}

                    <div className="ptw-game-footer">
                      {game.gameTime && <span className="ptw-game-time">{game.gameTime}</span>}
                      {game.venue && <span className="ptw-venue">{game.venue}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
